<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import CountryModel from '@models/Country';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.countries'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'order',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'name', label: this.$t('table_labels.name'), sortable: true },
        { key: 'name_de', label: this.$t('table_labels.name_de'), sortable: true },
        { key: 'iso_2_code', label: this.$t('table_labels.iso_2_code'), sortable: true },
        { key: 'order', label: this.$t('table_labels.order'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
      },
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    restore(id) {
      if (confirm(this.$t('alerts.restore_entity', { entity: this.$t('entities.country') }))) {
        this.$wait.start('country processing');
        new CountryModel({
          id: `${id}/restore`,
        })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('country processing');
          })
          .catch(() => {
            this.$wait.end('country processing');
          });
      }
    },
    itemsProvider(ctx) {
      return CountryModel.orderBy(this.sortDir + ctx.sortBy)
        .where('search', this.filters.search)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.country') }))) {
        this.$wait.start('country processing');
        const country = new CountryModel({ id });
        await country.delete();
        this.$wait.end('country processing');
        this.$refs.asyncTable.refresh();
      }
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.countries')" :button-text="$t('titles.create_country')" @button-pressed="$router.push({ name: 'countries.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span>&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [$t('search.name'), $t('search.name_de')].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="countries-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('countries processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(iso_2_code)="row">
          {{ row.item.iso_2_code }}
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'countries.edit', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn v-if="row.item.deleted_at === null" size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
            <b-btn v-else v-can:superAdministrate size="sm" variant="outline-success" @click.stop="restore(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'trash-restore']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
